import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ModuleMaterialsManager } from "./ModuleMaterialsManager";

interface ModuleFormData {
  title: string;
  description: string;
  lesson_content: string;
  category: string;
  difficulty: string;
  is_active: boolean;
  is_free: boolean;
}

const initialFormData: ModuleFormData = {
  title: "",
  description: "",
  lesson_content: "",
  category: "power_bi",
  difficulty: "beginner",
  is_active: true,
  is_free: false,
};

const categoryLabels: Record<string, string> = {
  power_bi: "Power BI",
  sql: "SQL",
  python: "Python",
};

const difficultyLabels: Record<string, string> = {
  beginner: "Iniciante",
  intermediate: "Intermediário",
  advanced: "Avançado",
};

export function AdminModuleManager() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ModuleFormData>(initialFormData);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: modules = [], isLoading } = useQuery({
    queryKey: ["admin-trail-modules"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("study_trail_modules")
        .select("*")
        .order("category")
        .order("order_index", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: ModuleFormData) => {
      const maxOrder = modules
        .filter(m => m.category === data.category)
        .reduce((max, m) => Math.max(max, m.order_index), -1);

      const { error } = await supabase.from("study_trail_modules").insert({
        title: data.title,
        description: data.description || null,
        lesson_content: data.lesson_content || null,
        category: data.category,
        difficulty: data.difficulty,
        is_active: data.is_active,
        is_free: data.is_free,
        order_index: maxOrder + 1,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-trail-modules"] });
      queryClient.invalidateQueries({ queryKey: ["study-trail"] });
      setIsDialogOpen(false);
      resetForm();
      toast({ title: "Módulo criado com sucesso!" });
    },
    onError: (error) => {
      toast({ title: "Erro ao criar módulo", description: String(error), variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ModuleFormData }) => {
      const { error } = await supabase
        .from("study_trail_modules")
        .update({
          title: data.title,
          description: data.description || null,
          lesson_content: data.lesson_content || null,
          category: data.category,
          difficulty: data.difficulty,
          is_active: data.is_active,
          is_free: data.is_free,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-trail-modules"] });
      queryClient.invalidateQueries({ queryKey: ["study-trail"] });
      setIsDialogOpen(false);
      resetForm();
      toast({ title: "Módulo atualizado!" });
    },
    onError: (error) => {
      toast({ title: "Erro ao atualizar", description: String(error), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("study_trail_modules").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-trail-modules"] });
      queryClient.invalidateQueries({ queryKey: ["study-trail"] });
      toast({ title: "Módulo removido!" });
    },
    onError: (error) => {
      toast({ title: "Erro ao remover", description: String(error), variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingId(null);
  };

  const handleEdit = (mod: any) => {
    setEditingId(mod.id);
    setFormData({
      title: mod.title,
      description: mod.description || "",
      lesson_content: mod.lesson_content || "",
      category: mod.category,
      difficulty: mod.difficulty,
      is_active: mod.is_active,
      is_free: mod.is_free ?? false,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.category) {
      toast({ title: "Preencha os campos obrigatórios", variant: "destructive" });
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">Gerenciar Módulos das Trilhas</CardTitle>
          <CardDescription>Crie e organize os módulos de estudo</CardDescription>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-2" /> Novo Módulo</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? "Editar Módulo" : "Novo Módulo"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Trilha *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="power_bi">Power BI</SelectItem>
                      <SelectItem value="sql">SQL</SelectItem>
                      <SelectItem value="python">Python</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nível *</Label>
                  <Select value={formData.difficulty} onValueChange={(v) => setFormData(prev => ({ ...prev, difficulty: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Iniciante</SelectItem>
                      <SelectItem value="intermediate">Intermediário</SelectItem>
                      <SelectItem value="advanced">Avançado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Título *</Label>
                <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Ex: Introdução ao Power BI" />
              </div>

              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Descrição breve do módulo..." rows={2} />
              </div>

              <div className="space-y-2">
                <Label>Conteúdo da Aula (Markdown)</Label>
                <Textarea value={formData.lesson_content} onChange={(e) => setFormData(prev => ({ ...prev, lesson_content: e.target.value }))} placeholder="## Título da Aula&#10;&#10;Escreva o conteúdo aqui usando Markdown..." rows={10} className="font-mono text-sm" />
              </div>

              <div className="flex items-center justify-between">
                <Label>Módulo ativo</Label>
                <Switch checked={formData.is_active} onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_active: c }))} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Módulo gratuito</Label>
                  <p className="text-xs text-muted-foreground">Acessível para todos os usuários logados</p>
                </div>
                <Switch checked={formData.is_free} onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_free: c }))} />
              </div>

              {editingId && (
                <div className="border-t pt-4">
                  <ModuleMaterialsManager moduleId={editingId} moduleTitle={formData.title} />
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" className="flex-1" disabled={isSubmitting}>
                  {isSubmitting ? "Salvando..." : editingId ? "Salvar" : "Criar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground text-center py-4">Carregando...</p>
        ) : modules.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">Nenhum módulo criado ainda.</p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Trilha</TableHead>
                  <TableHead>Nível</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Acesso</TableHead>
                  <TableHead className="w-24">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modules.map((mod) => (
                  <TableRow key={mod.id}>
                    <TableCell>
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                    <TableCell className="font-medium">{mod.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{categoryLabels[mod.category] || mod.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{difficultyLabels[mod.difficulty] || mod.difficulty}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={mod.is_active ? "default" : "secondary"}>
                        {mod.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={(mod as any).is_free ? "success" : "warning"}>
                        {(mod as any).is_free ? "Gratuito" : "PRO"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(mod)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { if (confirm("Excluir módulo?")) deleteMutation.mutate(mod.id); }} disabled={deleteMutation.isPending}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
